/**
 * Six Ways tile helpers, shared by the Awareness Landing, Read More and Grow a
 * Mo pages so all three report the same tile_id and carry attribution the same
 * way (DFE-97).
 *
 * The card is typed structurally rather than importing a page's model, so this
 * stays independent of the content templates.
 */

interface TileLike {
    title?: string;
    cta?: { btnText?: string, btnLink?: string } | null;
}

const ATTRIBUTION_STORAGE_KEY = 'mo-awareness-landing-attribution';

const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'gclid',
    'fbclid',
    'dclid',
    'ttclid',
    'wbraid',
];

export const captureInboundAttribution = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const captured: { [key: string]: string } = {};
        ATTRIBUTION_KEYS.forEach(key => {
            const value = params.get(key);
            if (value) captured[key] = value;
        });
        if (Object.keys(captured).length > 0) {
            window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(captured));
        }
    } catch (e) {
        // Ignore: UTM pass-through is best-effort.
    }
};

const readInboundAttribution = () => {
    try {
        return JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY) || '{}') as { [key: string]: string };
    } catch (e) {
        return {};
    }
};

const parseUrl = (url: string) => {
    try {
        return new URL(url, window.location.origin);
    } catch (e) {
        return null;
    }
};

export const isOutboundUrl = (url: string) => {
    const parsed = parseUrl(url);
    if (!parsed) return false;
    return !parsed.hostname.endsWith('movember.com');
};

const needsUtmPassthrough = (url: string) => {
    const parsed = parseUrl(url);
    if (!parsed) return false;
    return parsed.hostname === 'conversations.movember.com' || parsed.hostname === 'movember.store' || parsed.hostname.endsWith('.movember.store');
};

export const appendTileUtms = (url: string, tileId: string) => {
    if (!needsUtmPassthrough(url)) return url;
    const dest = parseUrl(url);
    if (!dest) return url;
    const inbound = readInboundAttribution();
    if (inbound.utm_source || inbound.utm_medium || inbound.utm_campaign) {
        ATTRIBUTION_KEYS.forEach(key => {
            if (key !== 'utm_content' && inbound[key]) {
                dest.searchParams.set(key, inbound[key]);
            }
        });
        dest.searchParams.set('utm_content', tileId);
    } else {
        dest.searchParams.set('utm_source', 'movember');
        dest.searchParams.set('utm_medium', 'referral');
        dest.searchParams.set('utm_campaign', 'awareness_raisers');
        dest.searchParams.set('utm_content', tileId);
    }
    return dest.toString();
};

export const getTileId = (card: TileLike) => {
    const title = (card.title || '').toLowerCase();
    const blob = `${card.title || ''} ${card.cta ? card.cta.btnText : ''} ${card.cta ? card.cta.btnLink : ''}`.toLowerCase();
    if (blob.includes('grow') && blob.includes('mo')) return 'grow_a_mo';
    if (blob.includes('social') || blob.includes('download posts') || blob.includes('impact-assets')) return 'share_socials';
    if (blob.includes('conversation')) return 'start_conversation';
    if (blob.includes('merch') || blob.includes('movember.store')) return 'wear_merch';
    // Title before href: a Nail the Five card copied with /posters must not
    // report as distribute_materials.
    if (title.includes('nail') || (title.includes('five') && title.includes('health')) || title.includes('healthier')) {
        return 'nail_the_five';
    }
    if (blob.includes('mens-health')) return 'nail_the_five';
    if (blob.includes('material') || blob.includes('poster') || blob.includes('view resources') || blob.includes('distribute')) return 'distribute_materials';
    return 'unknown';
};

const NAIL_THE_FIVE_PATH = '/mens-health/general';

export const getTileHref = (card: TileLike) => {
    const href = card.cta ? card.cta.btnLink || '' : '';
    // CMS sometimes pastes the posters URL onto Nail the Five. Send that tile
    // to the Nail the Five page instead of duplicating Distribute Materials.
    if (getTileId(card) === 'nail_the_five' && /\/posters(\/|$|\?)/i.test(href)) {
        return NAIL_THE_FIVE_PATH;
    }
    return href;
};
